import { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { techAPI } from '../../lib/api';
import { toast } from 'sonner';
import { X, Loader2, CheckCircle2, CreditCard, AlertTriangle } from 'lucide-react';

/* ─── Inner form rendered inside <Elements> ─────────────────────── */
function CardForm({ invoiceId, amount, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    // 1. Confirm with Stripe
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message);
      setProcessing(false);
      return;
    }

    if (!paymentIntent || paymentIntent.status !== 'succeeded') {
      setError('Payment was not completed. Please try again.');
      setProcessing(false);
      return;
    }

    // 2. Backend verification — this is the source of truth
    try {
      const res = await techAPI.confirmCardPayment(invoiceId, paymentIntent.id);
      onSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment confirmed but recording failed. Contact support.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        options={{
          layout: 'tabs',
          paymentMethodOrder: ['card'],
        }}
      />
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg border border-red-200" data-testid="card-payment-error">
          <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || !elements || processing}
        className="w-full h-14 bg-emerald-600 text-white font-bold rounded-lg active:bg-emerald-700 disabled:opacity-60 flex items-center justify-center gap-2"
        data-testid="card-payment-submit"
      >
        {processing ? (
          <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
        ) : (
          <><CreditCard className="w-5 h-5" /> Charge ${amount.toFixed(2)}</>
        )}
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={processing}
        className="w-full h-10 text-slate-500 text-sm font-bold active:text-slate-700 disabled:opacity-40"
        data-testid="card-payment-cancel"
      >
        Cancel
      </button>
    </form>
  );
}

/* ─── Main Modal ────────────────────────────────────────────────── */
export default function CardPaymentModal({ invoice, onSuccess, onCancel }) {
  const [step, setStep] = useState('amount'); // amount | loading | card | success
  const [amount, setAmount] = useState(String(invoice.balance_due || 0));
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [piId, setPiId] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const balanceDue = invoice.balance_due || 0;

  const startPayment = useCallback(async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < 0.50) {
      toast.error('Minimum card payment is $0.50');
      return;
    }
    if (amt > balanceDue + 0.01) {
      toast.error(`Amount exceeds balance due ($${balanceDue.toFixed(2)})`);
      return;
    }

    setStep('loading');
    setError(null);

    try {
      const res = await techAPI.createPaymentIntent(invoice.id, amt);
      const data = res.data;

      setClientSecret(data.client_secret);
      setPiId(data.payment_intent_id);

      // Initialize Stripe with the connected account (if available)
      const stripeOptions = data.connected_account_id
        ? { stripeAccount: data.connected_account_id }
        : {};
      const sp = loadStripe(data.publishable_key, stripeOptions);
      setStripePromise(sp);
      setStep('card');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start card payment');
      setStep('amount');
    }
  }, [amount, balanceDue, invoice.id]);

  const handleSuccess = (data) => {
    setResult(data);
    setStep('success');
    onSuccess(data);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center" data-testid="card-payment-modal">
      <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-emerald-600 px-5 py-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5" /> Charge Card
          </h3>
          {step !== 'loading' && (
            <button onClick={onCancel} className="p-1 rounded-full text-white/80 active:bg-white/10" data-testid="card-modal-close">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="px-5 py-4">
          {/* ── Step: Amount Entry ── */}
          {step === 'amount' && (
            <div data-testid="card-amount-step">
              <p className="text-xs text-slate-400 mb-1">Invoice #{invoice.invoice_number}</p>
              <p className="text-lg font-bold font-mono text-slate-950 mb-4">
                Balance Due: ${balanceDue.toFixed(2)}
              </p>
              {error && (
                <div className="flex items-start gap-2 p-3 mb-3 bg-red-50 rounded-lg border border-red-200">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 mb-1 block">Charge Amount</label>
                <div className="flex items-center h-12 border-2 border-slate-200 rounded-lg overflow-hidden focus-within:border-emerald-500">
                  <span className="px-3 text-slate-400 font-bold">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0.50"
                    max={balanceDue}
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    className="flex-1 h-full text-lg font-mono font-bold text-slate-950 focus:outline-none pr-3"
                    data-testid="card-amount-input"
                    autoFocus
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  {parseFloat(amount) > 0 && parseFloat(amount) < balanceDue && (
                    <span className="text-amber-500">Partial payment — remaining will be due after</span>
                  )}
                  {parseFloat(amount) >= balanceDue && (
                    <span className="text-emerald-600">Full balance</span>
                  )}
                </p>
              </div>
              <button
                onClick={startPayment}
                className="w-full h-14 mt-4 bg-emerald-600 text-white font-bold rounded-lg active:bg-emerald-700 flex items-center justify-center gap-2"
                data-testid="card-proceed-btn"
              >
                <CreditCard className="w-5 h-5" /> Enter Card Details
              </button>
            </div>
          )}

          {/* ── Step: Loading (creating PaymentIntent) ── */}
          {step === 'loading' && (
            <div className="py-8 text-center" data-testid="card-loading-step">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Setting up secure payment...</p>
            </div>
          )}

          {/* ── Step: Card Entry (Stripe PaymentElement) ── */}
          {step === 'card' && stripePromise && clientSecret && (
            <div data-testid="card-entry-step">
              <div className="mb-4">
                <p className="text-xs text-slate-400">Invoice #{invoice.invoice_number}</p>
                <p className="text-lg font-bold font-mono text-slate-950">
                  Charging ${parseFloat(amount).toFixed(2)}
                </p>
              </div>
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: {
                    theme: 'stripe',
                    variables: {
                      colorPrimary: '#059669',
                      borderRadius: '8px',
                      fontFamily: 'system-ui, sans-serif',
                    },
                  },
                }}
              >
                <CardForm
                  invoiceId={invoice.id}
                  amount={parseFloat(amount)}
                  onSuccess={handleSuccess}
                  onCancel={onCancel}
                />
              </Elements>
            </div>
          )}

          {/* ── Step: Success ── */}
          {step === 'success' && (
            <div className="text-center py-4" data-testid="card-payment-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-lg font-bold text-slate-950">Payment Successful</p>
              <p className="text-sm text-slate-500 mt-1">
                ${result?.amount?.toFixed(2)} charged to card
              </p>
              {result?.invoice_balance_due > 0 && (
                <p className="text-sm text-amber-600 mt-1">
                  Remaining balance: ${result.invoice_balance_due.toFixed(2)}
                </p>
              )}
              {result?.invoice_balance_due <= 0 && (
                <p className="text-sm text-emerald-600 mt-1 font-bold">Invoice fully paid</p>
              )}
              <button
                onClick={onCancel}
                className="w-full h-12 mt-4 bg-slate-100 text-slate-700 font-bold rounded-lg active:bg-slate-200"
                data-testid="card-payment-done"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
